import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Volume2, Play, Square, RefreshCw } from "lucide-react";

export default function AudioDemo() {
  const [voices, setVoices] = useState([]);
  const [frenchVoices, setFrenchVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [text, setText] = useState(
    "Bonjour ! Bienvenue chez Pressing Sakura. Je suis votre assistante virtuelle. Comment puis-je vous aider aujourd'hui ? Vous pouvez me poser des questions sur nos services, nos tarifs ou nos horaires de ramassage et de livraison."
  );
  const synthRef = useRef(window.speechSynthesis);

  // Charger les voix disponibles
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = synthRef.current.getVoices();
      setVoices(availableVoices);
      
      // Filtrer les voix françaises
      const french = availableVoices.filter(
        (v) => v.lang.startsWith("fr")
      );
      setFrenchVoices(french);
      
      // Sélectionner la première voix féminine française par défaut
      const femaleVoice = french.find(
        (v) => v.name.toLowerCase().includes("female") || 
               v.name.toLowerCase().includes("amélie") ||
               v.name.toLowerCase().includes("aurelie") ||
               v.name.toLowerCase().includes("thomas") === false
      );
      if (femaleVoice) {
        setSelectedVoice(femaleVoice.name);
      } else if (french.length > 0) {
        setSelectedVoice(french[0].name);
      }
    };

    // Les voix peuvent ne pas être chargées immédiatement
    loadVoices();
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices;
    }
    
    // Fallback pour certains navigateurs
    setTimeout(loadVoices, 100);
    setTimeout(loadVoices, 500);

    return () => {
      synthRef.current.cancel();
    };
  }, []);

  const speak = () => {
    if (synthRef.current.speaking) {
      synthRef.current.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = voices.find((v) => v.name === selectedVoice);
    
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.lang = "fr-FR";

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synthRef.current.speak(utterance);
  };

  const stop = () => {
    synthRef.current.cancel();
    setIsSpeaking(false);
  };

  const testAllFrenchVoices = async () => {
    for (const voice of frenchVoices) {
      const utterance = new SpeechSynthesisUtterance(
        `Ceci est la voix ${voice.name}.`
      );
      utterance.voice = voice;
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.lang = "fr-FR";
      
      synthRef.current.speak(utterance);
      
      // Attendre que cette voix finisse
      await new Promise((resolve) => {
        utterance.onend = resolve;
        utterance.onerror = resolve;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-pink-800 mb-2 flex items-center justify-center gap-2">
            <Volume2 className="h-8 w-8" />
            Démo Audio - Web Speech API
          </h1>
          <p className="text-pink-600">
            Teste les voix gratuites du navigateur pour le Chatbot
          </p>
        </div>

        {/* Infos sur les voix disponibles */}
        <Card className="border-pink-200">
          <CardHeader>
            <CardTitle className="text-pink-800">Voix disponibles</CardTitle>
            <CardDescription>
              {voices.length} voix au total, {frenchVoices.length} voix françaises détectées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {frenchVoices.map((v) => (
                <Badge 
                  key={v.name} 
                  variant={v.name === selectedVoice ? "default" : "outline"}
                  className={v.name === selectedVoice ? "bg-pink-600" : "border-pink-300 text-pink-700"}
                >
                  {v.name} ({v.lang})
                </Badge>
              ))}
              {frenchVoices.length === 0 && (
                <p className="text-amber-600 text-sm">
                  ⚠️ Aucune voix française détectée. Essaie de rafraîchir la page ou utilise Chrome/Edge.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contrôles */}
        <Card className="border-pink-200">
          <CardHeader>
            <CardTitle className="text-pink-800">Paramètres de la voix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sélection de la voix */}
            <div>
              <label className="block text-sm font-medium text-pink-700 mb-2">
                Voix sélectionnée
              </label>
              <Select value={selectedVoice || ""} onValueChange={setSelectedVoice}>
                <SelectTrigger className="border-pink-300">
                  <SelectValue placeholder="Sélectionne une voix" />
                </SelectTrigger>
                <SelectContent>
                  {frenchVoices.map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name} ({v.lang})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vitesse */}
            <div>
              <label className="block text-sm font-medium text-pink-700 mb-2">
                Vitesse : {rate.toFixed(1)}x
              </label>
              <Slider
                value={[rate]}
                onValueChange={([v]) => setRate(v)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
              />
            </div>

            {/* Pitch */}
            <div>
              <label className="block text-sm font-medium text-pink-700 mb-2">
                Tonalité : {pitch.toFixed(1)}
              </label>
              <Slider
                value={[pitch]}
                onValueChange={([v]) => setPitch(v)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
              />
            </div>

            {/* Texte à lire */}
            <div>
              <label className="block text-sm font-medium text-pink-700 mb-2">
                Texte à lire
              </label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                className="border-pink-300 focus:border-pink-500"
                placeholder="Entre le texte que tu veux entendre..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            onClick={speak}
            disabled={isSpeaking || !selectedVoice}
            className="bg-pink-600 hover:bg-pink-700"
          >
            <Play className="h-4 w-4 mr-2" />
            Écouter
          </Button>
          
          <Button
            onClick={stop}
            disabled={!isSpeaking}
            variant="outline"
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
          >
            <Square className="h-4 w-4 mr-2" />
            Arrêter
          </Button>
          
          <Button
            onClick={testAllFrenchVoices}
            disabled={isSpeaking || frenchVoices.length === 0}
            variant="outline"
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tester toutes les voix
          </Button>
        </div>

        {/* Info */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-amber-800 mb-2">💡 Bon à savoir</h3>
            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
              <li>Les voix disponibles dépendent de ton navigateur et système d'exploitation</li>
              <li>Chrome/Edge ont généralement plus de voix que Firefox/Safari</li>
              <li>Sur Windows, tu auras les voix Microsoft (Hortense, etc.)</li>
              <li>Sur Mac, tu auras les voix Apple (Amélie, Thomas, etc.)</li>
              <li>Cette API est 100% gratuite et fonctionne hors-ligne</li>
            </ul>
          </CardContent>
        </Card>

        {/* Lien retour */}
        <div className="text-center">
          <a href="/" className="text-pink-600 hover:text-pink-800 underline">
            ← Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
}
